import React from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Layers, PlugZap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ConnectorIcon from "@/components/data/ConnectorIcon";

const syncTone = (s) => s === "success" ? "border-[hsl(var(--success))] text-[hsl(var(--success))]" : "border-amber-500 text-amber-600";

// Prebuilt dashboards: model name + connectors (name, last sync). Same data for every role; Open model is SA-only.
export function PrebuiltSourcesView({ data, isSuperAdmin, onClose }) {
    const navigate = useNavigate();
    const models = data?.models || [];
    const connectors = data?.connectors || [];
    const impact = data?.impact || {};
    return (
        <div className="space-y-5 mt-5" data-testid="prebuilt-sources-view">
            {(impact.widgets || 0) > 0 && (
                <div className="text-xs text-muted-foreground" data-testid="prebuilt-sources-impact">
                    Powers <b className="text-foreground">{impact.widgets}</b> widget{impact.widgets === 1 ? "" : "s"}
                    {(impact.kpis || 0) > 0 && <> (<b className="text-foreground">{impact.kpis}</b> KPI{impact.kpis === 1 ? "" : "s"})</>} on this dashboard.
                </div>
            )}
            <section data-testid="prebuilt-sources-models">
                <div className="eyebrow mb-2 flex items-center gap-1.5"><Layers className="h-3 w-3" /> Data model</div>
                {models.length === 0 && <div className="text-sm text-muted-foreground">No data model found for this dashboard.</div>}
                {models.map((m) => (
                    <div key={m.id} className="border border-border rounded-sm bg-card px-3 py-2.5 mb-2 flex items-center justify-between gap-3" data-testid={`prebuilt-sources-model-${m.slug}`}>
                        <div className="min-w-0">
                            <div className="font-medium text-sm truncate" data-testid={`prebuilt-sources-model-name-${m.slug}`}>{m.name}</div>
                            <div className="text-[11px] text-muted-foreground">{(m.row_count || 0).toLocaleString()} rows available to you</div>
                        </div>
                        {isSuperAdmin && (
                            <Button variant="ghost" size="sm" className="rounded-sm h-7 text-xs" onClick={() => { onClose(); navigate(`/data-models?model=${m.slug}`); }}
                                data-testid={`sources-model-open-${m.slug}`}>
                                Open model <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                        )}
                    </div>
                ))}
            </section>
            {connectors.length > 0 && (
                <section data-testid="prebuilt-sources-connectors">
                    <div className="eyebrow mb-2 flex items-center gap-1.5"><PlugZap className="h-3 w-3" /> Connectors</div>
                    <ul className="border border-border rounded-sm divide-y divide-border bg-card">
                        {connectors.map((c) => (
                            <li key={c.id} className="flex items-center gap-2.5 px-3 py-2" data-testid={`prebuilt-sources-connector-${c.id}`}>
                                <span className="w-6 h-6 bg-secondary rounded-sm flex items-center justify-center overflow-hidden p-0.5 shrink-0">
                                    <ConnectorIcon iconUrl={c.icon_url} category={c.category} name={c.name} className="max-w-full max-h-full" />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm truncate">{c.name}</span>
                                    {c.last_sync_at && <span className="block text-[11px] text-muted-foreground">Last synced {new Date(c.last_sync_at).toLocaleString()}</span>}
                                </span>
                                {c.last_sync_status && <Badge variant="outline" className={`text-[10px] capitalize ${syncTone(c.last_sync_status)}`}>{c.last_sync_status}</Badge>}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}
